<!--
  Settings → Notifications, tab "Automatic mails": every mail the software
  sends on its own (app/helpers/automail.js, routes /api/automatic-mails), with
  its recipients, and the recipients of the Harvesting morning report.

  Lived in Dependency Sync until 2026-10-18. The robot mails (kind sumal_sync)
  are set with their robot, in Settings → Dependency Sync: the line links there.
  Emits `state` ({ failed }) for the tab bar: mails whose last send failed.
-->
<template>
  <vx-card>
    <hv-section
      title="Automatic mails"
      sub="Every mail the software sends on its own: what it is, when it leaves, who gets it, and when it last left. Click a line to change the recipients or switch the mail off.">
      <template slot="actions">
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw"
                   :disabled="mailsLoading" @click="loadMails">Refresh</vs-button>
      </template>
      <hv-table :rows="mails" :loading="mailsLoading" :error="mailsError" row-key="key"
                empty-icon="MailIcon" empty-text="No automatic mail.">
        <template slot="thead">
          <vs-th>Mail</vs-th><vs-th>When</vs-th><vs-th>Who receives</vs-th><vs-th>Last sent</vs-th><vs-th>7 days</vs-th><vs-th></vs-th>
        </template>
        <template slot="row" slot-scope="{ r }">
          <vs-td><b>{{ r.name }}</b><div class="hv2-muted">{{ r.what }}</div></vs-td>
          <vs-td>{{ r.when }}<div v-if="r.condition" class="hv2-muted">{{ r.condition }}</div></vs-td>
          <vs-td>
            <hv-pill v-if="!r.enabled" label="Off" icon="PauseIcon" />
            <template v-else>
              {{ r.to.length ? r.to.join(', ') : '—' }}
              <div v-if="r.cc.length" class="hv2-muted">cc {{ r.cc.join(', ') }}</div>
              <div v-if="r.overridden" class="hv2-muted">changed here</div>
            </template>
          </vs-td>
          <vs-td>
            {{ r.last_sent_at ? roWhen(r.last_sent_at) : DASH }}
            <div v-if="r.last_status && r.last_status !== 'sent'" class="hv2-sync-bad">{{ r.last_status }}</div>
          </vs-td>
          <vs-td class="hv2-col-n">{{ r.sent_7d }}<span v-if="r.skipped_7d" class="hv2-muted"> · {{ r.skipped_7d }} off</span></vs-td>
          <vs-td>
            <vs-button size="small" type="flat" icon-pack="feather" :icon="openMail === r.key ? 'icon-chevron-up' : 'icon-edit-2'"
                       @click="toggleMail(r)" />
          </vs-td>
        </template>
      </hv-table>

      <!-- The editor of the open line -->
      <div v-if="openMailRow" class="sumal-mail-editor">
        <div class="hv2-subhead">{{ openMailRow.name }}</div>

        <template v-if="openMailRow.kind === 'fixed'">
          <div class="sumal-settings-row">
            <vs-switch v-model="mailForm.enabled" />
            <span class="ml-2">{{ mailForm.enabled ? 'Sent' : 'Switched off. The software prepares it and does not send it.' }}</span>
          </div>
          <vs-textarea v-model="mailForm.recipients" label="Recipients, one per line" rows="3" class="w-full" />
          <vs-input v-model="mailForm.cc" label="Copy (cc), separated by ;" class="w-full mb-3" />
          <div class="sumal-settings-actions">
            <vs-button size="small" :disabled="mailSaving" @click="saveMail">{{ mailSaving ? 'Saving…' : 'Save' }}</vs-button>
            <vs-button v-if="openMailRow.overridden" size="small" type="border" class="ml-2" @click="resetMail">Back to the addresses in the code</vs-button>
            <span class="hv2-muted ml-3">In the code: {{ openMailRow.default_to.join(', ') }}<template v-if="openMailRow.default_cc.length"> · cc {{ openMailRow.default_cc.join(', ') }}</template></span>
          </div>
        </template>

        <template v-else-if="openMailRow.kind === 'sumal_sync'">
          <p class="hv2-sec-sub mb-3">The recipients, the mail mode and the hours of this mail are set with the robot, in Settings → Dependency Sync.</p>
          <vs-button size="small" type="border" icon-pack="feather" icon="icon-settings" @click="openRobot(openMailRow.sumal_source)">
            Open the settings of the robot
          </vs-button>
        </template>

        <template v-else>
          <p class="hv2-sec-sub">Who receives the morning report. An address already there is updated, not added twice. Nobody active: the report is still computed and sent to nobody.</p>
          <hv-table :rows="hvRecipients" :loading="hvLoading" :error="hvError" row-key="id"
                    empty-icon="MailIcon" empty-title="No recipient" empty-text="Nobody receives the report.">
            <template slot="thead">
              <vs-th>Address</vs-th><vs-th>Name</vs-th><vs-th>Role</vs-th><vs-th>Active</vs-th><vs-th></vs-th>
            </template>
            <template slot="row" slot-scope="{ r }">
              <vs-td>{{ r.email }}</vs-td>
              <vs-td>{{ r.name || '—' }}</vs-td>
              <vs-td><hv-pill :label="r.kind === 'cc' ? 'Copy' : 'To'" :tone="r.kind === 'cc' ? 'info' : 'ok'" /></vs-td>
              <vs-td><vs-switch :value="r.active" @input="toggleHvRecipient(r, $event)" /></vs-td>
              <vs-td><vs-button size="small" type="flat" color="danger" icon-pack="feather" icon="icon-trash-2" @click="confirmRemoveHvRecipient(r)" /></vs-td>
            </template>
          </hv-table>
          <div class="vx-row items-end mt-3">
            <div class="vx-col md:w-1/3 w-full mb-3">
              <vs-input class="w-full" label="E-mail" placeholder="name@xyloimpact.com" v-model="hvForm.email" @keyup.enter="addHvRecipient" />
            </div>
            <div class="vx-col md:w-1/4 w-full mb-3">
              <vs-input class="w-full" label="Name (optional)" v-model="hvForm.name" />
            </div>
            <div class="vx-col md:w-1/4 w-full mb-3">
              <vs-select v-model="hvForm.kind" label="Role" class="w-full">
                <vs-select-item value="to" text="To" />
                <vs-select-item value="cc" text="Copy" />
              </vs-select>
            </div>
            <div class="vx-col md:w-1/6 w-full mb-3">
              <vs-button class="w-full" :disabled="!hvValidEmail || hvSaving" @click="addHvRecipient">{{ hvSaving ? '…' : 'Add' }}</vs-button>
            </div>
          </div>
          <div class="sumal-settings-actions">
            <vs-button size="small" type="border" icon-pack="feather" icon="icon-eye" @click="previewReport">See the report</vs-button>
            <vs-button size="small" class="ml-2" icon-pack="feather" icon="icon-send" :disabled="!hvActiveTo.length || hvSending" @click="confirmSendReport">
              {{ hvSending ? 'Sending…' : 'Send now' }}
            </vs-button>
          </div>
        </template>
      </div>
    </hv-section>

    <mail-preview :open.sync="previewOpen" :loading="previewLoading" :subject="previewSubject" :html="previewHtml" />
  </vx-card>
</template>

<script>
import axios from '@/axios.js'
import HvSection from '@/views/harvesting2/components/HvSection'
import HvTable from '@/views/harvesting2/components/HvTable'
import HvPill from '@/views/harvesting2/components/HvPill'
import formatMixin from '@/views/harvesting2/formatMixin'
import MailPreview from './MailPreview'
import { roWhen } from '@/helpers/romaniaTime'

export default {
  name: 'AutomaticMails',
  components: { HvSection, HvTable, HvPill, MailPreview },
  mixins: [formatMixin],
  data () {
    return {
      mails: [],
      mailsLoading: false,
      mailsError: '',
      openMail: null,
      mailForm: { enabled: true, recipients: '', cc: '' },
      mailSaving: false,
      hvRecipients: [],
      hvLoading: false,
      hvError: '',
      hvForm: { email: '', name: '', kind: 'to' },
      hvSaving: false,
      hvSending: false,
      previewOpen: false,
      previewLoading: false,
      previewHtml: '',
      previewSubject: ''
    }
  },
  computed: {
    openMailRow () { return this.mails.find((m) => m.key === this.openMail) || null },
    hvValidEmail () { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.hvForm.email.trim()) },
    hvActiveTo () { return this.hvRecipients.filter((r) => r.active && r.kind !== 'cc') }
  },
  methods: {
    roWhen,
    notify (title, text, color) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check', position: 'bottom-right', time: 6000 })
    },
    errText (e) { return (e.response && e.response.data && e.response.data.message) || e.message },
    loadMails () {
      this.mailsLoading = true
      this.mailsError = ''
      axios.get('/automatic-mails')
        .then((r) => {
          this.mails = r.data || []
          this.$emit('state', { failed: this.mails.filter((m) => m.last_status === 'failed').length })
        })
        .catch((e) => { this.mailsError = this.errText(e) })
        .finally(() => { this.mailsLoading = false })
    },
    openRobot (key) {
      this.$router.push({ path: '/settings/dependency-sync', query: { service: key, view: 'settings' } }).catch(() => {})
    },
    toggleMail (r) {
      if (this.openMail === r.key) { this.openMail = null; return }
      this.openMail = r.key
      if (r.kind === 'fixed') {
        this.mailForm = { enabled: r.enabled, recipients: r.to.join('\n'), cc: r.cc.join('; ') }
      } else if (r.kind === 'harvesting_report') {
        this.loadHvRecipients()
      }
    },
    saveMail () {
      if (!this.openMailRow) return
      this.mailSaving = true
      axios.put(`/automatic-mails/${this.openMailRow.key}`, {
        enabled: this.mailForm.enabled,
        recipients: this.mailForm.recipients,
        cc: this.mailForm.cc
      })
        .then(() => { this.notify('Saved', this.openMailRow.name, 'success'); this.loadMails() })
        .catch((e) => this.notify('Not saved', this.errText(e), 'danger'))
        .finally(() => { this.mailSaving = false })
    },
    resetMail () {
      if (!this.openMailRow) return
      axios.delete(`/automatic-mails/${this.openMailRow.key}`)
        .then(() => { this.notify('Reset', 'Back to the addresses in the code', 'success'); this.openMail = null; this.loadMails() })
        .catch((e) => this.notify('Not reset', this.errText(e), 'danger'))
    },
    // Harvesting morning report: its own recipient table (harvesting2 routes).
    loadHvRecipients () {
      this.hvLoading = true
      this.hvError = ''
      axios.get('/harvesting2/report/recipients')
        .then((r) => { this.hvRecipients = r.data || [] })
        .catch((e) => { this.hvError = this.errText(e) })
        .finally(() => { this.hvLoading = false })
    },
    addHvRecipient () {
      if (!this.hvValidEmail) return
      this.hvSaving = true
      axios.post('/harvesting2/report/recipients', this.hvForm)
        .then(() => { this.hvForm = { email: '', name: '', kind: 'to' }; this.loadHvRecipients(); this.loadMails() })
        .catch((e) => this.notify('Not added', this.errText(e), 'danger'))
        .finally(() => { this.hvSaving = false })
    },
    toggleHvRecipient (r, value) {
      axios.post('/harvesting2/report/recipients', { email: r.email, name: r.name, kind: r.kind, active: value })
        .then(() => { this.loadHvRecipients(); this.loadMails() })
        .catch((e) => { this.notify('Not changed', this.errText(e), 'danger'); this.loadHvRecipients() })
    },
    confirmRemoveHvRecipient (r) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Remove this recipient?',
        text: `${r.email} will no longer receive the morning report.`,
        acceptText: 'Remove',
        cancelText: 'Cancel',
        accept: () => {
          axios.delete(`/harvesting2/report/recipients/${r.id}`)
            .then(() => { this.loadHvRecipients(); this.loadMails() })
            .catch((e) => this.notify('Not removed', this.errText(e), 'danger'))
        }
      })
    },
    previewReport () {
      this.previewOpen = true
      this.previewLoading = true
      this.previewHtml = ''
      this.previewSubject = 'Harvesting morning report, as it would go out now.'
      axios.get('/harvesting2/report/preview')
        .then((r) => { this.previewHtml = r.data.html })
        .catch((e) => { this.notify('Cannot show the report', this.errText(e), 'danger'); this.previewOpen = false })
        .finally(() => { this.previewLoading = false })
    },
    confirmSendReport () {
      const to = this.hvActiveTo.map((r) => r.email).join(', ')
      const cc = this.hvRecipients.filter((r) => r.active && r.kind === 'cc').map((r) => r.email)
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Send the morning report now?',
        text: `To: ${to}${cc.length ? `\nCopy: ${cc.join(', ')}` : ''}`,
        acceptText: 'Send',
        cancelText: 'Cancel',
        accept: () => this.sendReport()
      })
    },
    sendReport () {
      this.hvSending = true
      axios.post('/harvesting2/report/send')
        .then((r) => {
          if (r.data.sent) this.notify('Report sent', `To ${this.hvActiveTo.length} recipient(s).`, 'success')
          else this.notify('Not sent', r.data.reason || 'No active recipient.', 'warning')
          this.loadMails()
        })
        .catch((e) => this.notify('Not sent', this.errText(e), 'danger'))
        .finally(() => { this.hvSending = false })
    }
  },
  created () { this.loadMails() }
}
</script>
